﻿import { useCallback, useEffect, useMemo, useState } from 'react';
import { showError, showInfo, showSuccess } from '../../shared/ui/appToast';
import { preflightCatalogSnapshotForRead } from '../../catalog/infra-api';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import type { PromotionWithTargets, PromotionUpsertInput } from '../domain/promotionTypes';
import {
  deletePromotionIntake,
  listPromotionsCache,
  updatePromotionIntake,
} from '../infra/promotionsRepo';
import {
  formatPromotionListDetail,
  promotionStatusLabel,
} from './promotionPresentation';
import { detectPromotionConflicts, type PromotionConflict } from './promotionConflicts';
import { buildPromotionsListCsv } from '../infra/promotionsListCsv';
import { shareOrDownloadCsv } from '../../shared/csv-core/infra/shareCsv';
import { matchesSearchAny } from '../../shared/format/searchText';

export type PromotionListFilter = 'active' | 'inactive' | 'all';
export type PromotionSortKey = 'name' | 'kind' | 'scope' | 'status' | 'code' | 'priority' | 'endsAt';
export type PromotionSortDir = 'asc' | 'desc';
export type PromotionViewMode = 'table' | 'cards' | 'kanban';

const DEFAULT_PAGE_SIZE = 10 as const;

function toUpdateInput(
  promo: PromotionWithTargets,
  overrides: Partial<PromotionUpsertInput> = {}
): PromotionUpsertInput {
  return {
    name: promo.name,
    description: promo.description,
    kind: promo.kind,
    scope: promo.scope,
    valuePercent: promo.valuePercent,
    valueFixed: promo.valueFixed,
    comboBuyQty: promo.comboBuyQty,
    comboPayQty: promo.comboPayQty,
    comboBundlePrice: promo.comboBundlePrice,
    startsAt: promo.startsAt,
    endsAt: promo.endsAt,
    isActive: promo.isActive,
    branchIds: promo.branchIds,
    applyMode: promo.applyMode,
    promoCode: promo.promoCode,
    priority: promo.priority,
    stackable: promo.stackable,
    channels: promo.channels?.length ? promo.channels : undefined,
    conditions: promo.conditions,
    targets: promo.targets.map((t) => ({
      id: t.id,
      targetType: t.targetType,
      targetId: t.targetId,
    })),
    ...overrides,
  };
}

export function usePromotionsListScreen() {
  const { canView, canEdit } = useBranchPermission('Promotions');
  const canActivate = useModulePermission('Promotions', 'activate');
  const canExport = useModulePermission('Promotions', 'export');
  const currentBranchId = useSessionStore((state) => state.currentBranchId);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [promotions, setPromotions] = useState<PromotionWithTargets[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<PromotionListFilter>('active');
  const [sortKey, setSortKey] = useState<PromotionSortKey>('name');
  const [sortDir, setSortDir] = useState<PromotionSortDir>('asc');
  const [viewMode, setViewMode] = useState<PromotionViewMode>('table');
  const [historyPromotion, setHistoryPromotion] = useState<PromotionWithTargets | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState<5 | 10>(DEFAULT_PAGE_SIZE);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchMode, setBatchMode] = useState(false);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [editingPromotionId, setEditingPromotionId] = useState<string | null>(null);
  const [duplicateFromId, setDuplicateFromId] = useState<string | null>(null);
  const [createTemplateId, setCreateTemplateId] = useState<string | null>(null);

  const loadFn = useCallback(async (opts?: { force?: boolean }) => {
    try {
      await preflightCatalogSnapshotForRead({ force: opts?.force === true });
    } catch (e) {
      console.warn('[usePromotionsListScreen] preflight snapshot', e);
      // Cold cache + pull falló: no fingir "sin promos" — el preflight
      // exige error + Reintentar (module-audit X-CAT-P2b).
      const cached = await listPromotionsCache({ includeInactive: true }).catch(() => []);
      if (cached.length === 0) {
        setLoadError('No pudimos cargar las promociones. Revisá la conexión y reintentá.');
        return;
      }
    }
    setLoadError(null);
    let timedOut = false;
    try {
      const rows = await Promise.race([
        listPromotionsCache({ includeInactive: true }),
        new Promise<never>((_, reject) =>
          setTimeout(() => {
            timedOut = true;
            reject(new Error('timeout'));
          }, 8000)
        ),
      ]);
      setPromotions(rows);
      setTotalCount(rows.length);
    } catch (e: unknown) {
      if (timedOut) {
        setLoadError('Tardó demasiado. Probá de nuevo.');
      } else {
        const msg =
          e && typeof e === 'object' && 'message' in e
            ? String((e as { message?: string }).message)
            : 'No se pudieron cargar las promos.';
        setLoadError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const filteredPromotions = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = promotions.filter((promo) => {
      const status = promotionStatusLabel(promo);
      if (filter === 'active' && status !== 'Activa' && status !== 'Programada') return false;
      if (filter === 'inactive' && status !== 'Inactiva' && status !== 'Vencida') return false;
      if (!q) return true;
      return matchesSearchAny([promo.name, promo.promoCode, promo.description], search);
    });

    const dirMul = sortDir === 'asc' ? 1 : -1;
    filtered.sort((a, b) => {
      switch (sortKey) {
        case 'name':
          return a.name.localeCompare(b.name, 'es') * dirMul;
        case 'kind':
          return a.kind.localeCompare(b.kind) * dirMul;
        case 'scope':
          return a.scope.localeCompare(b.scope) * dirMul;
        case 'status': {
          const sa = promotionStatusLabel(a);
          const sb = promotionStatusLabel(b);
          return sa.localeCompare(sb, 'es') * dirMul;
        }
        case 'code': {
          const ca = a.promoCode?.trim() ?? '';
          const cb = b.promoCode?.trim() ?? '';
          return ca.localeCompare(cb) * dirMul;
        }
        case 'priority':
          return (a.priority - b.priority) * dirMul;
        case 'endsAt': {
          const ea = a.endsAt ? Date.parse(a.endsAt) : Infinity;
          const eb = b.endsAt ? Date.parse(b.endsAt) : Infinity;
          return (ea - eb) * dirMul;
        }
        default:
          return 0;
      }
    });

    return filtered;
  }, [filter, promotions, search, sortKey, sortDir]);

  useEffect(() => {
    setPageIndex(0);
  }, [filter, search, sortKey, sortDir]);

  const filteredTotalCount = filteredPromotions.length;
  const hasMore = (pageIndex + 1) * pageSize < filteredTotalCount;

  const paginatedPromotions = useMemo(
    () => filteredPromotions.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
    [filteredPromotions, pageIndex, pageSize]
  );

  const conflicts = useMemo<PromotionConflict[]>(
    // Solo promos que compiten hoy: vencidas y programadas no inflan el banner.
    () => detectPromotionConflicts(promotions),
    [promotions]
  );

  // Aviso dentro del form: el draft contra las promos existentes (excluye la
  // que se está editando). `liveOnly:false` para avisar también si una promo
  // programada va a pisar a esta cuando arranque.
  const checkDraftConflicts = useCallback(
    (draft: PromotionWithTargets): PromotionConflict[] =>
      detectPromotionConflicts(
        [draft, ...promotions.filter((p) => p.id !== draft.id)],
        { liveOnly: false }
      ).filter((c) => c.promotionA.id === draft.id || c.promotionB.id === draft.id),
    [promotions]
  );

  const listSummary = useMemo(() => {
    let activeCount = 0;
    let expiredCount = 0;
    let programmedCount = 0;
    for (const promo of promotions) {
      const status = promotionStatusLabel(promo);
      if (status === 'Activa') activeCount += 1;
      if (status === 'Programada') programmedCount += 1;
      if (status === 'Vencida') expiredCount += 1;
    }
    return { activeCount, expiredCount, programmedCount };
  }, [promotions]);

  const hasActiveFilters = filter !== 'active' || search.trim().length > 0;

  const onCreate = useCallback(() => {
    setEditingPromotionId(null);
    setDuplicateFromId(null);
    setCreateTemplateId(null);
    setFormModalVisible(true);
  }, []);

  const onCreateExample = useCallback(() => {
    setEditingPromotionId(null);
    setDuplicateFromId(null);
    setCreateTemplateId('bogo_2x1');
    setFormModalVisible(true);
  }, []);

  const onEdit = useCallback((promotionId: string) => {
    setDuplicateFromId(null);
    setEditingPromotionId(promotionId);
    setFormModalVisible(true);
  }, []);

  const onCloseFormModal = useCallback(() => {
    setFormModalVisible(false);
    setEditingPromotionId(null);
    setDuplicateFromId(null);
    setCreateTemplateId(null);
    void loadFn();
  }, [loadFn]);

  const onOpenHistory = useCallback((promo: PromotionWithTargets) => {
    setHistoryPromotion(promo);
  }, []);

  const onCloseHistory = useCallback(() => {
    setHistoryPromotion(null);
  }, []);

  const onToggleActive = useCallback(
    async (promo: PromotionWithTargets) => {
      if (!canActivate) return;
      const nextActive = !promo.isActive;
      try {
        await updatePromotionIntake(promo.id, toUpdateInput(promo, { isActive: nextActive }));
        // Optimistic: reflect toggle immediately. updatePromotionIntake already
        // synced the cache post-write; loadFn() would re-read from a possibly
        // stale cache and overwrite this optimistic state.
        setPromotions((prev) =>
          prev.map((p) => (p.id === promo.id ? { ...p, isActive: nextActive } : p)),
        );
        showSuccess(nextActive ? 'Promoción activada' : 'Promoción desactivada', promo.name);
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'message' in e
            ? String((e as { message?: string }).message)
            : 'No se pudo actualizar la promo.';
        showError('Error', msg);
      }
    },
    [canActivate]
  );

  const formatRowSubtitle = useCallback(
    (promo: PromotionWithTargets) => formatPromotionListDetail(promo),
    []
  );

  const onDelete = useCallback(
    async (promo: PromotionWithTargets) => {
      if (!canEdit) return;
      try {
        await deletePromotionIntake(promo.id);
        // Optimistic: deletePromotionIntake ya marcó deleted_at en el cache
        // RAM; loadFn() re-lee el mismo snapshot — acá basta filtrar la fila.
        setPromotions((prev) => prev.filter((p) => p.id !== promo.id));
        setTotalCount((prev) => Math.max(0, prev - 1));
        setSelectedIds((prev) => {
          if (!prev.has(promo.id)) return prev;
          const next = new Set(prev);
          next.delete(promo.id);
          return next;
        });
        showSuccess('Promoción eliminada', promo.name);
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'message' in e
            ? String((e as { message?: string }).message)
            : 'No se pudo eliminar la promo.';
        showError('Error al eliminar', msg);
      }
    },
    [canEdit]
  );

  const onDuplicate = useCallback(
    (promo: PromotionWithTargets) => {
      if (!canEdit) return;
      setEditingPromotionId(null);
      setDuplicateFromId(promo.id);
      setFormModalVisible(true);
    },
    [canEdit]
  );

  const clearFilters = useCallback(() => {
    setSearch('');
    setFilter('active');
    setPageIndex(0);
  }, []);

  const onSortChange = useCallback(
    (key: PromotionSortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir('asc');
      }
    },
    [sortKey]
  );

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === filteredPromotions.length) return new Set();
      return new Set(filteredPromotions.map((p) => p.id));
    });
  }, [filteredPromotions]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const enterBatchMode = useCallback(() => {
    setBatchMode(true);
    setViewMode('table');
  }, []);

  const exitBatchMode = useCallback(() => {
    setBatchMode(false);
    setSelectedIds(new Set());
  }, []);

  const onBatchToggleActive = useCallback(
    async (active: boolean) => {
      if (!canActivate || selectedIds.size === 0) return;
      const targets = promotions.filter((p) => selectedIds.has(p.id));
      let ok = 0;
      let fail = 0;
      for (const promo of targets) {
        try {
          await updatePromotionIntake(promo.id, toUpdateInput(promo, { isActive: active }));
          ok++;
        } catch {
          fail++;
        }
      }
      // Optimistic: reflect batch toggle immediately in local state.
      // updatePromotionIntake already synced the cache post-write per promo.
      setPromotions((prev) =>
        prev.map((p) => (selectedIds.has(p.id) ? { ...p, isActive: active } : p)),
      );
      exitBatchMode();
      if (fail === 0) {
        showSuccess(`${ok} promos ${active ? 'activadas' : 'desactivadas'}`);
      } else {
        showError('No se pudieron cambiar todas', `${ok} bien, ${fail} no.`);
      }
    },
    [canActivate, selectedIds, promotions, exitBatchMode]
  );

  const onExportCsv = useCallback(async () => {
    if (!canExport) {
      showError('Sin permisos', 'No podés exportar promos.');
      return;
    }
    if (filteredPromotions.length === 0) {
      showInfo('Exportar', 'Con estos filtros no hay promos para exportar.');
      return;
    }
    try {
      const csv = buildPromotionsListCsv(filteredPromotions);
      await shareOrDownloadCsv(csv, 'promociones.csv');
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message)
          : 'No se pudo generar el archivo. Reintentá.';
      showError('Exportar', msg);
    }
  }, [canExport, filteredPromotions]);

  const goToPreviousPage = useCallback(() => {
    setPageIndex((p) => Math.max(0, p - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPageIndex((p) => ((p + 1) * pageSize < filteredTotalCount ? p + 1 : p));
  }, [filteredTotalCount, pageSize]);

  const setPageSizeAndReset = useCallback((size: 5 | 10) => {
    setPageSize(size);
    setPageIndex(0);
  }, []);

  return {
    canView,
    canEdit,
    canActivate,
    canExport,
    loading,
    loadError,
    loadFn,
    search,
    setSearch,
    filter,
    setFilter,
    sortKey,
    sortDir,
    onSortChange,
    viewMode,
    setViewMode,
    promotions: paginatedPromotions,
    totalCount,
    filteredTotalCount,
    pageIndex,
    pageSize,
    setPageSize: setPageSizeAndReset,
    hasMore,
    goToPreviousPage,
    goToNextPage,
    listSummary,
    hasActiveFilters,
    clearFilters,
    onCreate,
    onCreateExample,
    onEdit,
    onToggleActive,
    onDelete,
    onDuplicate,
    checkDraftConflicts,
    onExportCsv,
    onOpenHistory,
    historyPromotion,
    onCloseHistory,
    formatRowSubtitle,
    batchMode,
    selectedIds,
    selectedCount: selectedIds.size,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
    enterBatchMode,
    exitBatchMode,
    onBatchToggleActive,
    conflicts,
    formModalVisible,
    editingPromotionId,
    duplicateFromId,
    createTemplateId,
    onCloseFormModal,
  };
}

export type PromotionsListScreenViewModel = ReturnType<typeof usePromotionsListScreen>;
